// Typed DTOs for the CHOOSE_ACTION prompt payload, matching wss-contract.md.

export interface MoveTarget {
  x: number;
  y: number;
  cost: number;
}

export interface TackleOdds {
  attackerFallsPercent: number;
  attackerStumblesPercent: number;
  lockUpPercent: number;
  defenderStumblesPercent: number;
  defenderFallsPercent: number;
}

export interface TackleTarget {
  targetId: string;
  odds: TackleOdds;
}

export interface ThrowTarget {
  x: number;
  y: number;
  passChancePercent: number;
  catchChancePercent: number | null;
}

export interface LegalActions {
  move: MoveTarget[];
  tackle: TackleTarget[];
  throw: ThrowTarget[];
  canStandUp: boolean;
  canTakeKnee: boolean;
  canEndActivation: boolean;
}

export interface ActionPromptData {
  eligiblePlayerIds: string[];
  playerId?: string;
  legalActionsByPlayer: Record<string, LegalActions>;
}

type ParseResult =
  | { ok: true; data: ActionPromptData }
  | { ok: false; error: string };

function parseLegalActions(raw: unknown, path: string): LegalActions | string {
  if (typeof raw !== "object" || raw === null) return `${path}: expected object`;
  const r = raw as Record<string, unknown>;

  if (!Array.isArray(r["move"])) return `${path}.move: expected array`;
  if (!Array.isArray(r["tackle"])) return `${path}.tackle: expected array`;
  if (!Array.isArray(r["throw"])) return `${path}.throw: expected array`;
  if (typeof r["canStandUp"] !== "boolean") return `${path}.canStandUp: expected boolean`;
  if (typeof r["canTakeKnee"] !== "boolean") return `${path}.canTakeKnee: expected boolean`;
  if (typeof r["canEndActivation"] !== "boolean") return `${path}.canEndActivation: expected boolean`;

  const move: MoveTarget[] = [];
  const moveArr = r["move"] as unknown[];
  for (let i = 0; i < moveArr.length; i++) {
    const elem = moveArr[i];
    if (typeof elem !== "object" || elem === null) return `${path}.move[${i}]: expected object`;
    const m = elem as Record<string, unknown>;
    if (typeof m.x !== "number" || typeof m.y !== "number" || typeof m.cost !== "number")
      return `${path}.move[${i}]: expected {x,y,cost}`;
    move.push({ x: m.x, y: m.y, cost: m.cost });
  }

  const tackle: TackleTarget[] = [];
  const tackleArr = r["tackle"] as unknown[];
  for (let i = 0; i < tackleArr.length; i++) {
    const elem = tackleArr[i];
    if (typeof elem !== "object" || elem === null) return `${path}.tackle[${i}]: expected object`;
    const t = elem as Record<string, unknown>;
    if (typeof t.targetId !== "string") return `${path}.tackle[${i}].targetId: expected string`;
    const o = t.odds;
    if (typeof o !== "object" || o === null) return `${path}.tackle[${i}].odds: expected object`;
    const odds = o as Record<string, unknown>;
    if (typeof odds.attackerFallsPercent !== "number") return `${path}.tackle[${i}].odds.attackerFallsPercent: expected number`;
    if (typeof odds.attackerStumblesPercent !== "number") return `${path}.tackle[${i}].odds.attackerStumblesPercent: expected number`;
    if (typeof odds.lockUpPercent !== "number") return `${path}.tackle[${i}].odds.lockUpPercent: expected number`;
    if (typeof odds.defenderStumblesPercent !== "number") return `${path}.tackle[${i}].odds.defenderStumblesPercent: expected number`;
    if (typeof odds.defenderFallsPercent !== "number") return `${path}.tackle[${i}].odds.defenderFallsPercent: expected number`;
    tackle.push({
      targetId: t.targetId,
      odds: {
        attackerFallsPercent: odds.attackerFallsPercent,
        attackerStumblesPercent: odds.attackerStumblesPercent,
        lockUpPercent: odds.lockUpPercent,
        defenderStumblesPercent: odds.defenderStumblesPercent,
        defenderFallsPercent: odds.defenderFallsPercent,
      },
    });
  }

  const throwTargets: ThrowTarget[] = [];
  const throwArr = r["throw"] as unknown[];
  for (let i = 0; i < throwArr.length; i++) {
    const elem = throwArr[i];
    if (typeof elem !== "object" || elem === null) return `${path}.throw[${i}]: expected object`;
    const t = elem as Record<string, unknown>;
    if (typeof t.x !== "number" || typeof t.y !== "number") return `${path}.throw[${i}]: expected {x,y}`;
    if (typeof t.passChancePercent !== "number") return `${path}.throw[${i}].passChancePercent: expected number`;
    if (t.catchChancePercent !== null && typeof t.catchChancePercent !== "number")
      return `${path}.throw[${i}].catchChancePercent: expected number or null`;
    throwTargets.push({
      x: t.x,
      y: t.y,
      passChancePercent: t.passChancePercent,
      catchChancePercent: t.catchChancePercent as number | null,
    });
  }

  return {
    move,
    tackle,
    throw: throwTargets,
    canStandUp: r["canStandUp"] as boolean,
    canTakeKnee: r["canTakeKnee"] as boolean,
    canEndActivation: r["canEndActivation"] as boolean,
  };
}

export function parseActionPromptData(raw: Record<string, unknown>): ParseResult {
  const eligiblePlayerIds = raw["eligiblePlayerIds"];
  if (!Array.isArray(eligiblePlayerIds) || !eligiblePlayerIds.every((v) => typeof v === "string"))
    return { ok: false, error: "eligiblePlayerIds: expected string[]" };

  const playerId = raw["playerId"];
  if (playerId !== undefined && playerId !== null && typeof playerId !== "string")
    return { ok: false, error: "playerId: expected string or absent" };

  const legalActionsByPlayerRaw = raw["legalActionsByPlayer"];
  if (typeof legalActionsByPlayerRaw !== "object" || legalActionsByPlayerRaw === null)
    return { ok: false, error: "legalActionsByPlayer: expected object" };

  const legalActionsByPlayer: Record<string, LegalActions> = {};
  for (const [pid, val] of Object.entries(legalActionsByPlayerRaw as Record<string, unknown>)) {
    const result = parseLegalActions(val, `legalActionsByPlayer[${pid}]`);
    if (typeof result === "string") return { ok: false, error: result };
    legalActionsByPlayer[pid] = result;
  }

  return {
    ok: true,
    data: {
      eligiblePlayerIds: eligiblePlayerIds as string[],
      playerId: typeof playerId === "string" ? playerId : undefined,
      legalActionsByPlayer,
    },
  };
}
